import React, { useEffect, useRef, useState } from "react";
import { X, ScanFace, CheckCircle2 } from "lucide-react";
import { useFace } from "@/contexts/FaceContext";
import { captureDescriptor, bestDistance, FACE_MATCH_THRESHOLD } from "@/lib/face";

const REGISTER_SAMPLES = 3;     // capture 3 samples for robust enrollment
const VERIFY_AUTO_MS = 600;     // continuous detection cadence
const VERIFY_CONFIRM_FRAMES = 2; // need 2 consecutive good matches to accept

// mode: "register" | "verify"
// onDone for register: { descriptors: number[][] }
// onDone for verify:   { matched, distance }
export function FaceScanner({ open, mode = "verify", referenceDescriptor, onDone, onClose, title }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const loopRef = useRef(null);
  const { ready } = useFace();
  const [statusMsg, setStatusMsg] = useState(
    mode === "register" ? "Look at the camera, then tap capture" : "Position your face inside the frame",
  );
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [samples, setSamples] = useState([]);   // for register
  const [liveDistance, setLiveDistance] = useState(null);
  const matchStreak = useRef(0);

  // Refs (avoid stale closures)
  const sampleRef = useRef(samples);
  useEffect(() => { sampleRef.current = samples; }, [samples]);

  // Camera lifecycle
  useEffect(() => {
    if (!open) {
      setSamples([]);
      matchStreak.current = 0;
      setLiveDistance(null);
      setSuccess(false);
      setStatusMsg(mode === "register" ? "Look at the camera, then tap capture" : "Position your face inside the frame");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (e) {
        setStatusMsg("Camera access denied. Allow camera in your browser settings.");
      }
    })();
    return () => {
      cancelled = true;
      if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Verify mode: continuous auto-scan loop with live distance feedback
  useEffect(() => {
    if (!open || mode !== "verify" || !ready || !window.faceapi) return;
    matchStreak.current = 0;
    loopRef.current = setInterval(async () => {
      if (busy || !videoRef.current || videoRef.current.readyState < 2) return;
      try {
        const live = await captureDescriptor(videoRef.current);
        if (!live) {
          setLiveDistance(null);
          setStatusMsg("No face detected — move closer & look at the camera");
          matchStreak.current = 0;
          return;
        }
        const d = bestDistance(live, referenceDescriptor);
        setLiveDistance(d);
        if (d <= FACE_MATCH_THRESHOLD) {
          matchStreak.current += 1;
          setStatusMsg(`Matching… (${d.toFixed(2)})`);
          if (matchStreak.current >= VERIFY_CONFIRM_FRAMES) {
            clearInterval(loopRef.current); loopRef.current = null;
            setBusy(true);
            setSuccess(true);
            setStatusMsg("Verified ✓");
            setTimeout(() => {
              onDone({ matched: true, distance: d });
              setBusy(false); setSuccess(false);
            }, 500);
          }
        } else {
          matchStreak.current = 0;
          setStatusMsg(`Not matched (d=${d.toFixed(2)}) — keep looking at camera`);
        }
      } catch (_) {
        /* swallow per-frame errors */
      }
    }, VERIFY_AUTO_MS);
    return () => { if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, ready, referenceDescriptor]);

  // Manual capture (register mode + simulation fallback)
  const onCapture = async () => {
    if (busy) return;
    setBusy(true);
    setStatusMsg("Capturing…");
    try {
      if (!ready || !window.faceapi) {
        setStatusMsg("Simulation mode — captured");
        setSuccess(true);
        setTimeout(() => {
          if (mode === "register") onDone({ descriptors: null, simulated: true });
          else onDone({ matched: true, simulated: true, distance: 0 });
          setSuccess(false); setBusy(false);
        }, 600);
        return;
      }
      const desc = await captureDescriptor(videoRef.current);
      if (!desc) {
        setStatusMsg("No face detected — try again");
        setBusy(false);
        return;
      }
      if (mode === "register") {
        const next = [...sampleRef.current, desc];
        setSamples(next);
        if (next.length >= REGISTER_SAMPLES) {
          setSuccess(true);
          setStatusMsg(`All ${REGISTER_SAMPLES} samples captured ✓`);
          setTimeout(() => {
            onDone({ descriptors: next });
            setSuccess(false); setBusy(false);
          }, 700);
        } else {
          setStatusMsg(`Sample ${next.length}/${REGISTER_SAMPLES} captured — slightly tilt your head & capture again`);
          setBusy(false);
        }
      } else {
        // Manual verify capture
        const d = bestDistance(desc, referenceDescriptor);
        setLiveDistance(d);
        if (d > FACE_MATCH_THRESHOLD) {
          setStatusMsg(`Face not matched (d=${d.toFixed(2)}) — try again`);
          setBusy(false);
          return;
        }
        setSuccess(true);
        setStatusMsg("Verified ✓");
        setTimeout(() => {
          onDone({ matched: true, distance: d });
          setSuccess(false); setBusy(false);
        }, 500);
      }
    } catch (e) {
      setStatusMsg("Scan failed — try again");
      setBusy(false);
    }
  };

  if (!open) return null;

  const accent = success ? "#22D3A5" : mode === "verify" && liveDistance != null && liveDistance <= FACE_MATCH_THRESHOLD ? "#22D3A5" : "#4F8EF7";

  return (
    <div className="fixed inset-0 z-[60] bg-black" data-testid="face-scanner">
      <video
        ref={videoRef}
        playsInline muted autoPlay
        className="absolute inset-0 w-full h-full object-cover"
        style={{ transform: "scaleX(-1)" }}
      />
      <div className="absolute inset-0 bg-black/40" />

      {/* Frame brackets */}
      <div className="absolute inset-0 flex items-center justify-center px-8">
        <div className="relative" style={{ width: 260, height: 320 }}>
          {[
            "top-0 left-0 border-t-4 border-l-4 rounded-tl-3xl",
            "top-0 right-0 border-t-4 border-r-4 rounded-tr-3xl",
            "bottom-0 left-0 border-b-4 border-l-4 rounded-bl-3xl",
            "bottom-0 right-0 border-b-4 border-r-4 rounded-br-3xl",
          ].map((cls, i) => (
            <div key={i} className={`absolute w-14 h-14 animate-bracket ${cls}`} style={{ borderColor: accent }} />
          ))}
          <div className="absolute inset-x-4 top-0 overflow-hidden" style={{ height: "100%" }}>
            <div
              className="absolute left-0 right-0 h-1 animate-scanline"
              style={{
                background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                boxShadow: `0 0 12px ${accent}`,
              }}
            />
          </div>
        </div>
      </div>

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 pt-safe">
        <div className="flex items-center justify-between px-4 h-14">
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center text-white" data-testid="face-scanner-close">
            <X size={18} />
          </button>
          <div className="text-white text-sm font-bold tracking-tight">
            {title || (mode === "register" ? `Register Face (${samples.length}/${REGISTER_SAMPLES})` : "Face Verify")}
          </div>
          <div className="w-10" />
        </div>
      </div>

      {/* Live distance pill (verify mode only) */}
      {mode === "verify" && liveDistance !== null && !success && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full text-xs font-mono"
          style={{
            background: liveDistance <= FACE_MATCH_THRESHOLD ? "rgba(34,211,165,0.2)" : "rgba(247,90,90,0.2)",
            color: liveDistance <= FACE_MATCH_THRESHOLD ? "#22D3A5" : "#F75A5A",
            border: `1px solid ${liveDistance <= FACE_MATCH_THRESHOLD ? "rgba(34,211,165,0.4)" : "rgba(247,90,90,0.4)"}`,
          }}
          data-testid="face-distance"
        >
          {liveDistance <= FACE_MATCH_THRESHOLD ? "✓ MATCH" : "× NO MATCH"} · d={liveDistance.toFixed(2)} · thr={FACE_MATCH_THRESHOLD}
        </div>
      )}

      {/* Bottom status + capture */}
      <div className="absolute bottom-0 inset-x-0 pb-safe">
        <div className="px-6 pb-6 flex flex-col items-center gap-3">
          <div className="text-center text-sm" style={{ color: "#E8ECFF" }} data-testid="face-status-text">
            {statusMsg}
          </div>
          {mode === "register" && (
            <div className="flex gap-2">
              {Array.from({ length: REGISTER_SAMPLES }).map((_, i) => (
                <div key={i}
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: i < samples.length ? "#22D3A5" : "rgba(255,255,255,0.25)" }}
                />
              ))}
            </div>
          )}
          {(mode === "register" || !ready) && (
            <button
              onClick={onCapture}
              disabled={busy}
              data-testid="face-capture-button"
              className="w-20 h-20 rounded-full flex items-center justify-center active:scale-95 transition-transform disabled:opacity-60"
              style={{ background: accent, boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}
              aria-label="Capture"
            >
              {success ? <CheckCircle2 className="text-white" size={32} /> : <ScanFace className="text-white" size={28} />}
            </button>
          )}
          {mode === "verify" && ready && (
            <div className="text-[10px] uppercase tracking-widest text-center" style={{ color: "rgba(255,255,255,0.6)" }}>
              Auto-scanning… stay still and look at the camera
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
